import { useState } from 'react';

interface ChecklistItem {
  question: string;
  answer: string;
}

interface ChecklistCategory {
  name: string;
  questions: string[];
}

export default function SelfDiagnosis() {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<ChecklistItem[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    agree: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const categories: ChecklistCategory[] = [
    {
      name: '위험성평가',
      questions: [
        '최근 6개월 내 위험성평가 보고서에 개선 담당자와 완료 기한이 명시돼 있나요?',
        '증빙 사진은 개선 전·후 비교로 정리돼 있나요?',
        '협력업체 근로자까지 평가 범위에 포함했습니까?',
        '근로자 참여 문서에 근로자 대표 서명이 포함돼 있나요?',
        '결과 문서에 재평가 주기와 보완 이력이 기록돼 있나요?'
      ]
    },
    {
      name: '근골격계유해요인조사',
      questions: [
        '최근 3년 이내 실시한 근골격계유해요인조사 결과에 개선계획과 실행 담당자·기한이 명시돼 있나요?',
        '조사 과정에서 근로자 대표가 참여한 회의록이 작성돼 있고, 서명이 포함돼 있나요?',
        '유해요인 조사 시 작업동작·작업시간·휴식시간 등 실제 작업 조건이 반영돼 있나요?',
        '과거 조사 결과의 개선조치 이행 여부가 문서나 사진으로 추적 가능하게 관리되고 있나요?',
        '조사 후 교육·훈련이나 개선활동이 이루어졌음을 증빙할 자료(사진, 교육일지)가 준비돼 있나요?'
      ]
    },
    {
      name: '안전보건체계구축',
      questions: [
        '안전보건방침과 목표가 문서화돼 있고, 경영책임자 결재·서명이 포함돼 있나요?',
        '정기적으로 이사회·경영진 보고 체계가 운영되며 기록이 남아 있나요?',
        '위험성평가, 근골격계조사 등 세부 실행 절차와 증빙 관리가 한 시스템 안에서 관리되고 있나요?',
        '근로자 의견 수렴 절차(협의체, 회의록 등)가 정기적으로 기록·보관되고 있나요?',
        '사고·재해 발생 시 재발방지 대책 수립·이행 이력을 문서나 사진으로 추적할 수 있나요?'
      ]
    },
    {
      name: '유해위험방지계획서',
      questions: [
        '신규 설비·공정 착수 전에 유해위험방지계획서 제출을 완료했나요?',
        '제출한 계획서에 공정 개요·위험요인·예방대책이 모두 포함돼 있나요?',
        '실제 현장 상황과 계획서 내용이 일치하며, 감독관 요구 시 증빙으로 제시할 수 있나요?',
        '과거 제출한 계획서의 보완·재작성 이력이 문서로 관리되고 있나요?',
        '계획서에 따른 안전설비 설치·개선조치가 완료됐음을 사진·점검표로 증명할 수 있나요?'
      ]
    },
    {
      name: '안전관리 위탁(대행)',
      questions: [
        '우리 사업장은 상시근로자 수 기준으로 안전·보건관리자 선임 의무가 있는지 확인했나요?',
        '법적으로 선임 의무가 있음에도, 외부 위탁 계약으로 관리자를 대신 운영 중인가요?',
        '위탁기관과의 계약서에 관리 범위·업무 내용·보고 체계가 명확히 규정돼 있나요?',
        '위탁기관이 매월 또는 분기별로 점검보고서·교육기록·증빙자료를 제공하고 있나요?',
        '위탁 계약의 유효기간과 재계약 이력이 문서로 관리되고 있으며, 감독관 요구 시 즉시 제시할 수 있나요?'
      ]
    },
    {
      name: 'KCs인증',
      questions: [
        '우리 사업장이 제조·수입하는 제품이 자율안전확인 대상 품목인지 확인했나요?',
        '제품 출고 전에 자율안전확인신고서를 작성·제출했나요?',
        '신고 시 요구되는 시험성적서·기술문서를 최신 상태로 갖추고 있나요?',
        '신고 완료 후 발급되는 자율안전확인번호와 명판을 제품에 부착했나요?',
        '과거 신고 제품의 변경 이력(모델 변경·사양 변경)을 관리하고 있으며, 필요 시 재신고를 진행했나요?'
      ]
    }
  ];

  const handleCategoryChange = (categoryName: string) => {
    setSelectedCategory(categoryName);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setSubmitSuccess(false);
  };

  const handleAnswer = (answer: string) => {
    const category = categories.find(cat => cat.name === selectedCategory);
    if (!category) return;

    const newAnswers = [...answers, {
      question: category.questions[currentQuestionIndex],
      answer: answer
    }];
    setAnswers(newAnswers);

    // 다음 질문으로 이동
    if (currentQuestionIndex < category.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const getCurrentCategory = () => {
    return categories.find(cat => cat.name === selectedCategory);
  };

  const isAllQuestionsAnswered = () => {
    const category = getCurrentCategory();
    return category && answers.length === category.questions.length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCategory) {
      alert('체크리스트 유형을 선택해주세요.');
      return;
    }

    if (!isAllQuestionsAnswered()) {
      alert('모든 질문에 답변해주세요.');
      return;
    }

    if (!formData.name || !formData.phone || !formData.email) {
      alert('모든 필수 정보를 입력해주세요.');
      return;
    }

    if (!formData.agree) {
      alert('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const answersText = answers
        .map((item, index) => `Q${index + 1}. ${item.question}\nA: ${item.answer}`)
        .join('\n\n');

      const formDataToSend = new URLSearchParams();
      formDataToSend.append('체크리스트유형', selectedCategory);
      formDataToSend.append('성함', formData.name);
      formDataToSend.append('연락처', formData.phone);
      formDataToSend.append('이메일주소', formData.email);
      formDataToSend.append('답변내용', answersText);
      formDataToSend.append('개인정보동의', formData.agree ? '동의' : '미동의');

      const response = await fetch('https://readdy.ai/api/form/d5934hm9uelhi9993m10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', phone: '', email: '', agree: false });
        setAnswers([]);
        setCurrentQuestionIndex(0);
      } else {
        alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const category = getCurrentCategory();
  const currentQuestion = category ? category.questions[currentQuestionIndex] : '';

  return (
    <section id="self-diagnosis" className="py-12 md:py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 md:mb-6">
            자가진단 체크리스트
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 font-medium leading-relaxed px-4">
            우리 사업장의 안전보건 수준을<br className="sm:hidden" /> 무료로 진단해보세요
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 lg:p-12">
          {submitSuccess ? (
            <div className="text-center py-12">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  제출이 완료되었습니다!
                </h3>
                <p className="text-base md:text-lg text-gray-600 mb-8">
                  곧 상세한 분석 리포트를 보내드리겠습니다.
                </p>
              </div>
              <button
                onClick={() => setSubmitSuccess(false)}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                새로운 진단 시작하기
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form>
              {/* 카테고리 선택 */}
              <div className="mb-6 md:mb-8">
                <label className="block text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">
                  점검이 필요한 분야를 선택해주세요 <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm md:text-base cursor-pointer"
                  required
                >
                  <option value="">체크리스트 유형을 선택해주세요</option>
                  {categories.map((category) => (
                    <option key={category.name} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 질문 표시 */}
              {selectedCategory && !isAllQuestionsAnswered() && (
                <div className="mb-6 md:mb-8">
                  <div className="mb-4 md:mb-6">
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900">
                        {selectedCategory}
                      </h3>
                      <span className="text-xs md:text-sm text-gray-500 font-medium">
                        질문 {currentQuestionIndex + 1} / {category?.questions.length}
                      </span>
                    </div>
                    
                    <div className="p-4 md:p-6 bg-blue-50 rounded-xl mb-4 md:mb-6">
                      <p className="text-sm md:text-base lg:text-lg text-gray-800 leading-relaxed">
                        {currentQuestion}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                      <button
                        type="button"
                        onClick={() => handleAnswer('예')}
                        className="py-3 md:py-4 px-4 md:px-6 bg-blue-600 text-white rounded-lg text-base md:text-lg font-bold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        예
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAnswer('아니오')}
                        className="py-3 md:py-4 px-4 md:px-6 bg-gray-200 text-gray-800 rounded-lg text-base md:text-lg font-bold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        아니오
                      </button>
                    </div>
                  </div>

                  {/* 진행 상황 표시 */}
                  {answers.length > 0 && (
                    <div className="mt-4 md:mt-6 p-3 md:p-4 bg-gray-50 rounded-lg">
                      <p className="text-xs md:text-sm text-gray-600 mb-2">답변 완료</p>
                      <div className="space-y-1.5 md:space-y-2">
                        {answers.map((item, index) => (
                          <div key={index} className="flex items-center gap-2 text-xs md:text-sm">
                            <span className="text-gray-500">Q{index + 1}.</span>
                            <span className={`font-medium ${item.answer === '예' ? 'text-blue-600' : 'text-gray-600'}`}>
                              {item.answer}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 연락처 정보 */}
              {selectedCategory && isAllQuestionsAnswered() && (
                <>
                  <div className="mb-4 md:mb-6 p-4 md:p-6 bg-green-50 rounded-xl text-center">
                    <p className="text-green-700 font-bold text-base md:text-lg">
                      ✓ 모든 질문에 답변하셨습니다!
                    </p>
                    <p className="text-gray-600 text-xs md:text-sm mt-2">
                      상세한 분석 리포트를 받으실 연락처를 입력해주세요
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                    <div>
                      <label className="block text-sm md:text-base font-bold text-gray-900 mb-2">
                        성함 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm md:text-base"
                        placeholder="이름을 입력해주세요"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm md:text-base font-bold text-gray-900 mb-2">
                        연락처 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm md:text-base"
                        placeholder="000-0000-0000"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-4 md:mb-6">
                    <label className="block text-sm md:text-base font-bold text-gray-900 mb-2">
                      이메일 주소 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm md:text-base"
                      placeholder="example@email.com"
                      required
                    />
                  </div>

                  {/* 개인정보 동의 */}
                  <div className="mb-6 md:mb-8">
                    <label className="flex items-start gap-2 md:gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.agree}
                        onChange={(e) => setFormData({ ...formData, agree: e.target.checked })}
                        className="mt-1 w-4 h-4 md:w-5 md:h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        required
                      />
                      <span className="text-xs md:text-sm text-gray-700">
                        <span className="font-bold text-gray-900">개인정보 수집 및 이용에 관한 동의</span>
                        <span className="text-red-500"> *</span>
                        <br />
                        수집항목: 성함, 연락처, 이메일 주소 | 이용목적: 자가진단 결과 제공 및 상담 | 보유기간: 3년
                      </span>
                    </label>
                  </div>

                  {/* 제출 버튼 */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-3 md:py-4 rounded-lg text-base md:text-lg font-bold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? '제출 중...' : '진단 결과 받기'}
                  </button>
                </>
              )}
            </form>
          )}
        </div>

        {/* 안내 문구 */}
        <div className="mt-6 md:mt-8 text-center">
          <p className="text-gray-600 text-xs md:text-sm px-4">
            * 체크리스트 결과를 바탕으로 업종별 유의사항과 개선방안을 담은 상세 리포트를 제공해드립니다.
          </p>
        </div>
      </div>
    </section>
  );
}
